<template>
  <v-row no-gutters>
    <v-col cols="12" class="pr-6">
      <card-title-row :straightTitle="$i18n.t('sidebar.prints.prints')" class="pt-5" />
      <sidebar-form ref="form" :inputs="inputsFiltered" v-model="printParams" :is-editing-on="!isStickyButtonsLoading">
        <template #printType="scope">
          <v-radio-group class="my-0 py-0" v-model="printType" dense hide-details :disabled="isStickyButtonsLoading">
            <template v-for="(item, idx) of scope.items">
              <v-col :key="idx" cols="12" class="px-0 py-1">
                <v-row no-gutters>
                  <v-radio :label="item.text" :value="item.value">
                    <template #label>
                      <span class="text-body-2 font--text">{{ item.text }}</span>
                    </template>
                  </v-radio>
                </v-row>
                <v-row no-gutters v-if="item.hint" class="pl-8">
                  <span class="font-italic font-weight-light radio--hint">{{ item.hint }}</span>
                </v-row>
              </v-col>
            </template>
          </v-radio-group>
        </template>
        <template #geometry="scope">
          <sidebar-geometry-input
            ref="printsLineGeometry"
            v-bind="scope"
            v-model="printParams.geometry"
            :is-valid.sync="isGeometryValid"
          />
        </template>
        <template #north_arrow="scope">
          <data-input v-bind="scope" v-model="printParams['north_arrow']">
            <template #selection="{ item }">
              <img v-if="item.value" width="20px" height="20px" :src="getNorthArrowImage(item.value)" class="mr-2" />
              {{ item.text }}
            </template>
            <template #item="{ item }">
              <img v-if="item.value" width="20px" height="20px" :src="getNorthArrowImage(item.value)" class="mr-2" />
              <span class="text-body-2" :class="{ 'pl-7': !item.value }">{{ item.text }}</span>
            </template>
          </data-input>
        </template>
        <template #selectAll="scope">
          <data-input
            :value="isAnyCheckboxSelected"
            :on-icon="isAnyCheckboxSelected && !areAllCheckboxesSelected ? 'mdi-minus-box' : 'mdi-checkbox-marked'"
            v-bind="scope"
            @input="toogleSelectAll"
          />
        </template>
      </sidebar-form>
    </v-col>
  </v-row>
</template>
<script>
import { call, get, sync } from 'vuex-pathify';
import DataInput from '@/components/DataInput';
export default {
  name: 'Prints',
  components: {
    DataInput,
    CardTitleRow: () => import('@/components/CardTitleRow'),
    SidebarForm: () => import('@/components/SidebarForm'),
    SidebarGeometryInput: () => import('@/components/SidebarGeometryInput'),
  },
  computed: {
    stickyButtons: sync('sidebar/stickyButtons'),
    isStickyButtonsLoading: sync('sidebar/isStickyButtonsLoading'),
    isStickyButtonsVisible: sync('sidebar/isStickyButtonsVisible'),
    isStickyButtonsValid: sync('sidebar/isStickyButtonsValid'),
    isSerialPrintingEnabled: get('admin/settingsValues@serial_printing_2_enabled'),
    isRibbonPrintingEnabled: get('admin/settingsValues@ribbon_printing_2_enabled'),
    project: get('layers/project'),
    token: get('authentication/token'),
    mapCenter: get('map/center'),
    zoom: get('map/zoom'),
    northArrows: get('admin/modulesMapping@printing.north_arrows'),
    northArrowItems() {
      const mappedNorthArrows = this.northArrows.map((northArrow, idx) => {
        return {
          text: this.isPrintingTwoEnabled
            ? this.$i18n.t('sidebar.prints.arrow', { suffix: idx + 1 })
            : northArrow.split('.')[0],
          value: northArrow,
        };
      });
      mappedNorthArrows.unshift({ text: this.$i18n.t('sidebar.prints.none'), value: '' });
      return mappedNorthArrows;
    },
    basemapLayer: get('layers/basemapLayer'),
    isBasemapLayerVisible() {
      return this.project?.basemap_visible;
    },
    isMapPrintModuleEnabled: get('admin/additionalModules@MAP_PRINT.enabled'),
    isPrintingTwoModuleEnabled: get('admin/additionalModules@PRINTING_2_MODULE.enabled'),
    isPrintingTwoSettingsEnabled: get('admin/settingsValues@printing_2_module_enabled'),
    isPrintingTwoEnabled() {
      return this.isPrintingTwoModuleEnabled && this.isPrintingTwoSettingsEnabled;
    },
    isAnyCheckboxSelected() {
      return this.printingCheckboxes.some(param => !!this.printParams[param]);
    },
    areAllCheckboxesSelected() {
      return this.printingCheckboxes.every(param => !!this.printParams[param]);
    },
    inputs() {
      return [
        {
          name: 'printType',
          hasDivider: true,
          hasNameColumn: false,
          cols: '12',
          items: [
            { text: this.$i18n.t('sidebar.prints.singlePrint'), value: 'single' },
            ...(this.isSerialPrintingEnabled || this.isMapPrintModuleEnabled
              ? [
                  {
                    text: this.$i18n.t('sidebar.prints.serialPrint'),
                    value: 'serial',
                    hint: this.$i18n.t('sidebar.prints.serialPrintHint'),
                  },
                ]
              : []),
            ...(this.isRibbonPrintingEnabled || this.isMapPrintModuleEnabled
              ? [
                  {
                    text: this.$i18n.t('sidebar.prints.ribbonPrint'),
                    value: 'ribbon',
                    hint: this.$i18n.t('sidebar.prints.ribbonPrintHint'),
                  },
                ]
              : []),
          ],
        },
        {
          name: 'geometry',
          dataType: { name: 'text' },
          hasNameColumn: false,
          icon: 'mdi-map-marker',
          geometryType: 'LineString',
          cols: '12',
          hint: this.$i18n.t('sidebar.nmtHint'),
          printTypesFilter: ['serial', 'ribbon'],
          ...(this.printType === 'ribbon' ? { maxPoints: 2, disableAddingNewVertices: true } : {}),
        },
        {
          name: 'title',
          dataType: { name: 'text' },
          translationPath: 'sidebar.prints.title',
          cols: '7',
          nameCols: '5',
          clearable: true,
          hideDetails: true,
        },
        {
          name: 'paper_format',
          dataType: { name: 'select' },
          items: [
            { text: 'A4', value: 'a4' },
            { text: 'A3', value: 'a3' },
            { text: 'A2', value: 'a2' },
            { text: 'A1', value: 'a1' },
            { text: 'A0', value: 'a0' },
          ],
          translationPath: 'sidebar.prints.printFormat',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          disabledSorting: true,
          printTypesFilter: ['serial', 'single'],
        },
        {
          name: 'output_format',
          dataType: { name: 'select' },
          items: [
            { text: 'PDF', value: 'pdf' },
            { text: 'PNG', value: 'png' },
            { text: 'JPEG', value: 'jpeg' },
          ],
          translationPath: 'sidebar.prints.fileFormat',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
        },
        {
          name: 'orientation',
          dataType: { name: 'select' },
          items: [
            { text: this.$i18n.t('sidebar.prints.vertical'), value: 'vertical' },
            { text: this.$i18n.t('sidebar.prints.horizontal'), value: 'horizontal' },
          ],
          translationPath: 'sidebar.prints.orientation',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          printTypesFilter: ['serial', 'single'],
        },
        {
          name: 'scale',
          dataType: { name: 'select' },
          items: [
            { text: '1:250', value: 250 },
            { text: '1:500', value: 500 },
            { text: '1:1000', value: 1000 },
            { text: '1:2000', value: 2000 },
            { text: '1:5000', value: 5000 },
            { text: '1:10000', value: 10000 },
            { text: '1:15000', value: 15000 },
            { text: '1:20000', value: 20000 },
            { text: '1:25000', value: 25000 },
            { text: '1:40000', value: 40000 },
            { text: '1:50000', value: 50000 },
            { text: '1:100000', value: 100000 },
          ],
          translationPath: 'sidebar.prints.scale',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          disabledSorting: true,
        },
        {
          name: 'header_position',
          dataType: { name: 'select' },
          items: [
            { text: this.$i18n.t('sidebar.prints.atTopOfPage'), value: 'top' },
            { text: this.$i18n.t('sidebar.prints.atBottomOfPage'), value: 'bottom' },
          ],
          translationPath: 'sidebar.prints.headerPosition',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
        },
        {
          name: 'north_arrow',
          dataType: { name: 'select' },
          items: this.northArrowItems,
          translationPath: 'sidebar.prints.northArrow',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          disabledSorting: true,
          oneLineInput: false,
        },
        {
          name: 'map_rotation',
          dataType: { name: 'integer' },
          items: [],
          translationPath: 'sidebar.prints.mapRotation',
          cols: '7',
          nameCols: '5',
          clearable: false,
          hideDetails: true,
          allowNegative: false,
          maxValue: 360,
          printTypesFilter: ['serial', 'single'],
        },
        ...(this.isPrintingTwoEnabled && this.checkboxBugFixer
          ? [
              {
                name: 'selectAll',
                label: this.$i18n.t('sidebar.prints.selectAll'),
                dataType: { name: 'checkbox' },
                hasNameColumn: false,
                cols: '12',
                hideDetails: true,
              },
            ]
          : []),
        {
          name: 'show_legend',
          label: this.$i18n.t('sidebar.prints.showLegend'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
        },
        {
          name: 'show_logo',
          label: this.$i18n.t('sidebar.prints.showLogo'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
        },
        {
          name: 'show_date',
          label: this.$i18n.t('sidebar.prints.showDate'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
        },
        {
          name: 'show_user',
          label: this.$i18n.t('sidebar.prints.showUser'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
        },
        {
          name: 'hide_footer',
          label: this.$i18n.t('sidebar.prints.showHideFooter'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
          hasDivider: this.printType !== 'ribbon',
        },
        {
          name: 'split',
          label: this.$i18n.t('sidebar.prints.split'),
          dataType: { name: 'checkbox' },
          hasNameColumn: false,
          cols: '12',
          hideDetails: true,
          printTypesFilter: ['serial', 'single'],
        },
      ];
    },
    inputsFiltered() {
      return this.inputs.filter(input => !input.printTypesFilter || input.printTypesFilter.includes(this.printType));
    },
  },
  data: () => ({
    printExtentCenter: [],
    printType: 'single',
    printParams: {
      title: '',
      paper_format: 'a4',
      output_format: 'pdf',
      orientation: 'vertical',
      scale: 500,
      header_position: 'top',
      north_arrow: '',
      map_rotation: 0,
      show_legend: false,
      show_logo: false,
      show_date: false,
      show_user: false,
      hide_footer: false,
      split: false,
    },
    printingLayersAttributes: [
      'labels_visible',
      'layer_id',
      'line_direction',
      'opacity',
      'parameters',
      'service_layers_names',
      'service_type',
      'style',
      'type',
      'url',
      'visible',
    ],
    printingCheckboxes: ['show_legend', 'show_logo', 'show_date', 'show_user', 'hide_footer'],
    isGeometryValid: true,
    currentTaskId: null,
    checkboxBugFixer: true,
  }),
  methods: {
    mapPrint: call('map/mapPrint'),
    cancelMapPrint: call('celeryTasks/stopCeleryTask'),
    checkMapPrint: call('celeryTasks/getCeleryTaskStatus'),
    getMapPrint: call('celeryTasks/getCeleryTaskResult'),
    getNorthArrowImage(value) {
      return `${process.env.VUE_APP_API_URL}/settings/qgis_icons/${value}?token=${this.token}`;
    },
    updatePrintBox() {
      this.$root.$emit('updatePrintBox', { ...this.printParams, print_type: this.printType });
    },
    clearPrintBox() {
      this.$root.$emit('clearPrintBox');
    },
    updatePrintExtentCenter(center) {
      this.printExtentCenter = center;
    },
    getMinMaxZoomStyle(style) {
      const returnStyle = JSON.parse(JSON.stringify(style));
      const { minzoom, maxzoom } = returnStyle;
      if ((minzoom || 1) > this.zoom || (maxzoom || 28) < this.zoom) {
        if (returnStyle['fill-opacity']) returnStyle['fill-opacity'] = 0;
        if (returnStyle['fill-outline-opacity']) returnStyle['fill-outline-opacity'] = 0;
      }
      const { minzoom: labelMinzoom, maxzoom: labelMaxzoom } = returnStyle.labels || {};
      if ((labelMinzoom || 1) > this.zoom || (labelMaxzoom || 28) < this.zoom) {
        if (returnStyle.labels?.attributes?.length) returnStyle.labels.attributes = [];
      }
      return returnStyle;
    },
    toogleSelectAll(value) {
      this.checkboxBugFixer = false;
      if (this.isAnyCheckboxSelected && !this.areAllCheckboxesSelected) value = true;
      this.printingCheckboxes.forEach(param => {
        this.printParams[param] = value;
      });
      this.$nextTick(() => {
        this.checkboxBugFixer = true;
      });
    },
    async print() {
      this.isStickyButtonsLoading = true;
      const layers = JSON.parse(JSON.stringify(this.$_getFlatGroupsLayers(this.project.layers).layers))
        .filter(layer => layer.visible && (!!layer.geometry_type || layer.type === 'service_layer'))
        .reduce((total, layer) => {
          if (layer.style) {
            layer.style = this.getMinMaxZoomStyle(layer.style);
          }
          if (layer.style?.uniques) {
            Object.keys(layer.style.uniques.values).forEach(key => {
              layer.style.uniques.values[key] = this.getMinMaxZoomStyle(layer.style.uniques.values[key]);
            });
          } else if (layer.style?.ranges) {
            layer.style.ranges.values = layer.style.ranges.values.map(rangeStyle => {
              return this.getMinMaxZoomStyle(rangeStyle);
            });
          }
          Object.keys(layer).forEach(key => {
            if (!this.printingLayersAttributes.includes(key)) {
              delete layer[key];
            }
          });
          total.push(layer);
          return total;
        }, []);
      const centerCoords = this.printExtentCenter;
      const centerCoordsGeojson = {
        type: 'Point',
        crs: {
          type: 'name',
          properties: {
            name: this.$_config.defaultEpsg || 'EPSG:4326',
          },
        },
        coordinates: centerCoords,
      };
      let printParams = JSON.parse(JSON.stringify(this.printParams));
      const inputsFilteredNames = this.inputsFiltered.map(input => input.name);
      Object.keys(printParams).forEach(name => {
        if (!inputsFilteredNames.includes(name)) {
          delete printParams[name];
        }
      });
      if (this.printType === 'ribbon') {
        printParams = { ...printParams, map_rotation: 0, orientation: 'vertical', paper_format: 'a4', split: false };
        printParams.ribbon = printParams.geometry;
        delete printParams.geometry;
      }
      const payload = {
        ...{ ...printParams, hide_footer: !this.printParams.hide_footer },
        layers,
        map_center: centerCoordsGeojson,
      };
      payload.basemap = this.isBasemapLayerVisible ? this.basemapLayer.role : null;
      try {
        const taskId = await this.mapPrint({ payload, version: this.isPrintingTwoEnabled ? 'v2' : '' });
        this.currentTaskId = taskId;
        this.$store.set('snackbar/PUSH_MESSAGE!', {
          message: this.$i18n.t('snackbar.generatingPrint'),
        });
        const checkerId = Date.now();
        this.$store.set('asyncStatusChecker/PUSH_STATUS_CHECKER!', {
          id: checkerId,
          checkingStatusFunction: async () => {
            try {
              const status = await this.checkMapPrint(taskId);
              if (['REVOKED', 'FAILURE'].includes(status)) {
                this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
                this.$store.set('snackbar/PUSH_ERROR!', {
                  message: this.$i18n.t('snackbar.generatingPrintError'),
                });
                this.currentTaskId = null;
                this.isStickyButtonsLoading = false;
                throw Error(`Celery task status checker: ${status}`);
              }
              return status === 'SUCCESS';
            } catch (error) {
              this.currentTaskId = null;
              console.log('error', error);
            }
          },
          successCallback: async () => {
            this.$store.set('asyncStatusChecker/REMOVE_STATUS_CHECKER_BY_ID!', checkerId);
            try {
              await this.getMapPrint({ id: taskId });
              this.currentTaskId = null;
              this.isStickyButtonsLoading = false;
            } catch (error) {
              this.currentTaskId = null;
              this.isStickyButtonsLoading = false;
              console.log('error', error);
            }
          },
          interval: 5000,
        });
      } catch (error) {
        this.currentTaskId = null;
        this.isStickyButtonsLoading = false;
        console.log('error', error);
      }
    },
  },
  watch: {
    isGeometryValid(nV) {
      this.isStickyButtonsValid = nV;
    },
    printType(nV) {
      this.$refs['printsLineGeometry']?.deleteGeometry();
      this.updatePrintBox();
      delete this.printParams.geometry;
      this.isGeometryValid = nV === 'single';
    },
  },
  mounted() {
    this.$root.$off('print');
    this.$root.$off('updatePrintExtentCenter');
    this.$root.$on('print', this.print);
    this.$root.$on('updatePrintExtentCenter', this.updatePrintExtentCenter);
    this.stickyButtons = [
      {
        action: 'print',
        translationPath: 'sidebar.prints.print',
      },
    ];
    this.isStickyButtonsVisible = true;
    this.isStickyButtonsValid = true;
    this.updatePrintBox();
    this.$watch(
      vm => {
        const { paper_format, scale, orientation, map_rotation, geometry } = vm.printParams;
        return [paper_format, scale, orientation, map_rotation, geometry];
      },
      () => {
        this.updatePrintBox();
      }
    );
  },
  beforeDestroy() {
    if (this.currentTaskId) {
      this.cancelMapPrint(this.currentTaskId);
    }
    this.isStickyButtonsLoading = false;
    this.isStickyButtonsVisible = false;
    this.clearPrintBox();
  },
};
</script>
<style lang="scss" scoped>
.radio--hint {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.59);
}
::v-deep .v-input--checkbox .v-label {
  color: #202124;
}
</style>
